// Dropdown options and label helpers for the additional guest fields collected
// at booking (DEV2-755). Shared between the public booking form, the admin
// create/edit dialog, and the admin booking detail view.
import type { GuestInfo } from '@/shared/types';

type Occupation = NonNullable<GuestInfo['occupation']>;
type DocumentType = NonNullable<GuestInfo['documentType']>;

export const OCCUPATION_OPTIONS: ReadonlyArray<{ value: Occupation; label: string }> = [
  { value: 'postgraduate', label: 'Postgraduate' },
  { value: 'doctorate', label: 'Doctorate' },
  { value: 'bachelor', label: "Bachelor's degree" },
  { value: 'internship', label: 'Internship' },
  { value: 'summer-school', label: 'Summer School' },
  { value: 'professional', label: 'Professional' },
  { value: 'other', label: 'Other' },
];

export const DOCUMENT_TYPE_OPTIONS: ReadonlyArray<{ value: DocumentType; label: string }> = [
  { value: 'citizen-card', label: 'Citizen Card' },
  { value: 'passport', label: 'Passport' },
  { value: 'other', label: 'Other' },
];

export const occupationLabel = (value?: string): string =>
  OCCUPATION_OPTIONS.find((o) => o.value === value)?.label ?? value ?? '';

export const documentTypeLabel = (value?: string): string =>
  DOCUMENT_TYPE_OPTIONS.find((o) => o.value === value)?.label ?? value ?? '';

/** Per-field validation messages for one guest. */
export interface GuestFieldErrors {
  name?: string;
  email?: string;
  phone?: string;
  nationality?: string;
  dateOfBirth?: string;
  documentType?: string;
  documentNumber?: string;
  taxNumber?: string;
  emergencyContact?: string;
  occupation?: string;
  country?: string;
  address?: string;
  postalCode?: string;
  city?: string;
}

/**
 * Every field the guest fieldset asks for. The public booking form marks all of
 * them required, so this doubles as the completeness definition.
 *
 * ⚠️ Mirrored in `functions/src/utils/guestInfo.ts` (REQUIRED_GUEST_FIELDS);
 * `functions/` is a separate package and cannot import from here.
 */
export const REQUIRED_GUEST_FIELDS = [
  'name',
  'email',
  'phone',
  'nationality',
  'dateOfBirth',
  'documentType',
  'documentNumber',
  'taxNumber',
  'emergencyContact',
  'occupation',
  'country',
  'address',
  'postalCode',
  'city',
] as const satisfies ReadonlyArray<keyof GuestInfo>;

/** Shared phone-shape check, used for both Mobile and Emergency Contact. */
const phoneError = (value: string): string | undefined => {
  const clean = value.trim().replace(/[\s\-()]/g, '');
  if (!/^\+?\d+$/.test(clean)) return 'Must contain only digits';
  const digits = clean.replace('+', '');
  if (digits.length < 9) return 'Must be at least 9 digits';
  if (digits.length > 15) return 'Must be at most 15 digits';
  return undefined;
};

/**
 * Validate one guest's personal information. Shared by the public booking form
 * and the student "complete your details" dialog so the two cannot drift.
 * Returns an object with a message per invalid field; empty means valid.
 */
export const validateGuest = (guest: GuestInfo): GuestFieldErrors => {
  const e: GuestFieldErrors = {};

  if (!guest.name?.trim()) {
    e.name = 'Name is required';
  } else if (guest.name.trim().length < 2) {
    e.name = 'Name must be at least 2 characters';
  }

  if (!guest.email?.trim()) {
    e.email = 'Email is required';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guest.email)) {
    e.email = 'Please enter a valid email address';
  }

  if (!guest.phone?.trim()) e.phone = 'Mobile number is required';
  else e.phone = phoneError(guest.phone);

  if (!guest.emergencyContact?.trim()) e.emergencyContact = 'Emergency contact is required';
  else e.emergencyContact = phoneError(guest.emergencyContact);

  if (!guest.nationality) e.nationality = 'Nationality is required';
  if (!guest.country) e.country = 'Country is required';
  if (!guest.occupation) e.occupation = 'Occupation is required';
  if (!guest.documentType) e.documentType = 'Document type is required';

  if (!guest.dateOfBirth) {
    e.dateOfBirth = 'Date of birth is required';
  } else if (new Date(guest.dateOfBirth) > new Date()) {
    e.dateOfBirth = 'Date of birth must be in the past';
  }

  if (!guest.documentNumber?.trim()) e.documentNumber = 'ID / Passport number is required';

  if (!guest.taxNumber?.trim()) {
    e.taxNumber = 'Tax number is required';
  } else if (!/^\d{9}$/.test(guest.taxNumber.trim())) {
    e.taxNumber = 'Tax number must be 9 digits';
  }

  if (!guest.address?.trim()) e.address = 'Address is required';
  if (!guest.postalCode?.trim()) e.postalCode = 'Postal code is required';
  if (!guest.city?.trim()) e.city = 'City is required';

  // Drop keys left undefined so Object.keys(...) reflects only real errors.
  (Object.keys(e) as (keyof GuestFieldErrors)[]).forEach((k) => {
    if (e[k] === undefined) delete e[k];
  });
  return e;
};

/** Fields from REQUIRED_GUEST_FIELDS that this guest has no value for. */
export const missingGuestFields = (guest: GuestInfo): Array<keyof GuestInfo> =>
  REQUIRED_GUEST_FIELDS.filter((field) => !guest[field]?.toString().trim());

/**
 * True when the guest carries every field the booking form collects.
 *
 * Bookings created before the DEV2-1038 write-path fix only stored
 * name/email/phone, so this is what tells the student portal to ask for the
 * rest. Presence-only: it deliberately does not re-run format validation, so a
 * legacy value the form would now reject doesn't trap the student in the dialog.
 */
export const isGuestInfoComplete = (guest: GuestInfo | undefined): boolean =>
  !!guest && missingGuestFields(guest).length === 0;
